const normalizeOrigin = (value: string): string => value.replace(/\/+$/, "");

/** Public production origin when env vars are unset on Vercel production. */
export const PRODUCTION_SITE_ORIGIN = "https://stonewallspacesinitiative.se";

const LOCAL_DEV_PORTS = [3000, 3001, 3002, 3003, 3004, 3005] as const;

const isLoopbackHostname = (hostname: string): boolean => {
  const host = hostname.replace(/^\[|\]$/g, "").toLowerCase();
  return host === "localhost" || host === "127.0.0.1" || host === "::1";
};

/**
 * Parse a hostname or absolute URL into an origin (`https://example.com`).
 * `URL.origin` strips default ports (`:443` / `:80`).
 */
export const parseAppOrigin = (
  value?: null | string,
  fallbackProto: "http" | "https" = "https",
): null | string => {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  try {
    if (/^https?:\/\//i.test(trimmed)) {
      return normalizeOrigin(new URL(trimmed).origin);
    }

    return normalizeOrigin(new URL(`${fallbackProto}://${trimmed}`).origin);
  } catch {
    return null;
  }
};

const addOrigin = (origins: Set<string>, value?: null | string): void => {
  const origin = parseAppOrigin(value);
  if (origin) origins.add(origin);
};

const addWwwVariant = (origins: Set<string>, origin: string): void => {
  try {
    const url = new URL(origin);
    if (
      isLoopbackHostname(url.hostname) ||
      url.hostname.endsWith(".vercel.app")
    ) {
      return;
    }

    if (url.hostname.startsWith("www.")) {
      url.hostname = url.hostname.slice(4);
    } else if (url.hostname.includes(".")) {
      url.hostname = `www.${url.hostname}`;
    } else {
      return;
    }

    origins.add(normalizeOrigin(url.origin));
  } catch {
    // ignore
  }
};

/**
 * Canonical site origin for sitemap, robots, metadata, and JSON-LD.
 * Prefer `NEXT_PUBLIC_SITE_URL` on Vercel; production falls back to the live domain.
 */
export const getCanonicalSiteOrigin = (): string => {
  const fromEnv =
    process.env.NEXT_PUBLIC_SITE_URL?.trim() ||
    process.env.NEXT_PUBLIC_SERVER_URL?.trim();

  if (fromEnv) {
    return parseAppOrigin(fromEnv) ?? normalizeOrigin(fromEnv);
  }

  if (process.env.VERCEL_ENV === "production") {
    return PRODUCTION_SITE_ORIGIN;
  }

  if (process.env.VERCEL_URL) {
    return (
      parseAppOrigin(`https://${process.env.VERCEL_URL}`) ??
      normalizeOrigin(`https://${process.env.VERCEL_URL}`)
    );
  }

  if (process.env.VERCEL_PROJECT_PRODUCTION_URL) {
    return (
      parseAppOrigin(`https://${process.env.VERCEL_PROJECT_PRODUCTION_URL}`) ??
      PRODUCTION_SITE_ORIGIN
    );
  }

  return "http://localhost:3000";
};

/**
 * Every origin this app may be served from.
 * Payload cookie auth (CSRF) and marker capture must allow the live host,
 * Vercel deployment URL, and local Next ports — not only canonical `serverURL`.
 */
export const getTrustedAppOrigins = (): string[] => {
  const origins = new Set<string>();

  addOrigin(origins, getCanonicalSiteOrigin());
  addOrigin(origins, PRODUCTION_SITE_ORIGIN);
  addOrigin(origins, process.env.NEXT_PUBLIC_SITE_URL);
  addOrigin(origins, process.env.NEXT_PUBLIC_SERVER_URL);
  addOrigin(
    origins,
    process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : null,
  );
  addOrigin(
    origins,
    process.env.VERCEL_BRANCH_URL
      ? `https://${process.env.VERCEL_BRANCH_URL}`
      : null,
  );
  addOrigin(
    origins,
    process.env.VERCEL_PROJECT_PRODUCTION_URL
      ? `https://${process.env.VERCEL_PROJECT_PRODUCTION_URL}`
      : null,
  );

  const extra = process.env.PAYLOAD_CSRF_ORIGINS ?? "";
  for (const part of extra.split(/[,\s]+/)) {
    addOrigin(origins, part);
  }

  for (const origin of [...origins]) {
    addWwwVariant(origins, origin);
  }

  for (const port of LOCAL_DEV_PORTS) {
    origins.add(`http://localhost:${port}`);
    origins.add(`http://127.0.0.1:${port}`);
  }

  const envPort = Number(process.env.PORT);
  if (Number.isInteger(envPort) && envPort > 0) {
    origins.add(`http://localhost:${envPort}`);
    origins.add(`http://127.0.0.1:${envPort}`);
  }

  return [...origins];
};

/** Cookie auth on mutating requests only accepts Origins in Payload `csrf`. */
export const getPayloadCsrfOrigins = (): string[] => getTrustedAppOrigins();
